import React from 'react'
import { Badge, BadgeStyle } from '../simple/badge'
import { makeRequest, showAxiosErrorAsToast } from '../../request'
import { Button, ButtonStyle } from '../simple/button'
import { Toasts } from '../../providers/toast'
import { Select } from '../form/select'
import { FloatingLoader } from '../simple/loader'
import { StudentGroupRecipient } from '../../pages/home/compose/composeTypes'
import { useLanguages } from '../../providers/languages'

/**
 * A component that allows the user to create a custom recipient group based on school, language, and grades.
 * This is used inside the user selector to allow the user to select a custom group of students.
 *
 * @param schools - An array of schools to choose from.
 * @param grades - An optional array of grades to choose from. Each grade has a name and an array of school IDs.
 * @param addCustomRecipient - A function to call when the user selects a custom recipient group.
 */
export const CustomRecipientMaker: React.FC<{
  schools: { name: string; id: number }[]
  grades?: { grade: string; schoolIDs: number[] }[]
  addCustomRecipient: (recipient: StudentGroupRecipient) => void
}> = ({ schools, grades, addCustomRecipient }) => {
  if (schools.length === 0) return 'missing schools'

  const languages = useLanguages()
  const languageOptions = React.useMemo(
    () => [
      { value: 'any_language', name: 'All' },
      { value: 'any_multilingual', name: 'All Multilingual' },
      ...languages.getOptions(),
    ],
    [languages]
  )

  const schoolOptions = React.useMemo(() => {
    return ([{ id: 'any_school', name: 'All' }] as { name: string; id: number | string }[]).concat(schools)
  }, [schools])

  const [school, setSchool] = React.useState<{ name: string; value: number | string }>(
    schoolOptions.length > 2
      ? {
          name: schoolOptions[0].name,
          value: schoolOptions[0].id,
        }
      : { name: schoolOptions[1].name, value: schoolOptions[1].id }
  )
  const [selectedGrades, setSelectedGrades] = React.useState<Set<string>>(new Set(['All']))
  const [language, setLanguage] = React.useState<{ name: string; value: string }>(languageOptions[0])
  const [loading, setLoading] = React.useState<boolean>(false)

  const gradeOptions = React.useMemo(() => {
    if (!grades) return undefined
    const filteredGrades = grades.filter(
      (g) => school.value === 'any_school' || g.schoolIDs.includes(school.value as number)
    )
    const newOptions = ['All'].concat(filteredGrades.map((g) => g.grade))
    setSelectedGrades((sg) => {
      const n = new Set([...sg].filter((g) => newOptions.includes(g)))
      if (n.size === 0) n.add('All')
      return n
    })
    return newOptions
  }, [grades, school])

  const toggleGrade = (g: string) => {
    const newGrades = new Set(selectedGrades)
    if (g === 'All') {
      setSelectedGrades(new Set(['All']))
    } else {
      if (newGrades.has('All')) newGrades.delete('All')
      if (newGrades.has(g)) newGrades.delete(g)
      else newGrades.add(g)
      if (newGrades.size === 0) newGrades.add('All')
      setSelectedGrades(newGrades)
    }
  }

  const makeGroup = () => {
    setLoading(true)
    makeRequest<StudentGroupRecipient | string>('/frontend_api/buildCustomRecipient', {
      language: language.value,
      school: school.value,
      grades: selectedGrades,
    })
      .then((response) => {
        if (typeof response === 'string')
          Toasts.showToast({
            message: 'No students found for the selected filters',
            error: true,
          })
        else addCustomRecipient(response)
      })
      .catch(showAxiosErrorAsToast)
      .finally(() => setLoading(false))
  }

  return (
    <div className="relative flex max-w-80 flex-col gap-2 p-1">
      {loading && <FloatingLoader background="bg-white/30" />}
      {schools.length > 1 && (
        <div className="flex items-center gap-2">
          <b className="min-w-24">School </b>
          <Select
            className="min-w-0 flex-grow"
            small
            options={schoolOptions.map((s) => ({ value: s.id, name: s.name }))}
            value={school}
            onChange={setSchool}
          />
        </div>
      )}
      <div className="flex items-center gap-2">
        <b className="min-w-24">Language </b>
        <Select className="min-w-0 flex-grow" small options={languageOptions} value={language} onChange={setLanguage} />
      </div>
      {gradeOptions && (
        <div className="flex items-center gap-2">
          <b className="min-w-24">Grades </b>
          <div className="flex flex-wrap gap-1">
            {gradeOptions.map((g) => (
              <Badge
                key={g}
                badgeStyle={selectedGrades.has(g) ? BadgeStyle.blue : BadgeStyle.lightDarkText}
                onClick={() => toggleGrade(g)}
                className="!px-2 !py-1.5"
              >
                {g}
              </Badge>
            ))}
          </div>
        </div>
      )}
      <Button small buttonStyle={ButtonStyle.blue} className="justify-center" onClick={makeGroup}>
        Select
      </Button>
    </div>
  )
}
